<template>
  <div id="invoices">
    <b-container v-show="!canViewInvoices">
      <b-alert show variant="danger">
        {{ getContentItem("MissingInvoiceAccess") }}
      </b-alert>
    </b-container>
    <b-container v-show="showShippingDetails">
      <lanter-shipping-detail
        :lanter-shipping-details="rowShippingDetails"
        :invoice-number="rowInvoiceNumber"
        :invoice-id="rowInvoiceId"
        @return="ReturnFromDetails"
      />
    </b-container>
    <b-container v-if="canViewInvoices" v-show="!showShippingDetails">
      <b-row>
        <b-col xs="12" lg="3">
          <OrdersMenu />
          <div class="mt-4">
            <OrdersSearchWidgetForm :dealerFilter="this.dealerFilters" />
          </div>
        </b-col>
        <b-col xs="12" lg="9">
          <h1>{{ getContentItem("invoicesButton") }}</h1>
          <div class="invoice-filters order-filters">
            <b-row>
              <b-col xs="12" lg="4">
                <MonthSelector number-of-months="12" :value="currentMonthSelection" @selectMonth="SetSelectedMonth" />
              </b-col>
            </b-row>
            <b-row>
              <b-col xs="12" lg="10">
                <AvailableDealerCodeSelector
                  :show-all-branches-option="hasAccessToAllBranches"
                  :default-dealercode="this.currentDealerSelection.value"
                  @selectDealerCode="SetSelectedDealerCode"
                />
              </b-col>
              <b-col xs="12" lg="2">
                <b-button id="filterButton" class="btn btn-view" @click="ApplyFilters">
                  {{ getContentItem("viewButton") }}
                </b-button>
              </b-col>
            </b-row>
          </div>
          <b-row>
            <b-col xs="12">
              <b-alert id="invoiceMessage" variant="secondary" show>
                {{ message }}
              </b-alert>
            </b-col>
          </b-row>
          <b-row>
            <b-col xs="12">
              <form :action="exportAction" method="POST" target="_blank">
                <input type="hidden" name="accountId" :value="activeAccountId" />
                <input type="hidden" name="userId" :value="user.UserId" />
                <input
                  v-for="invoice in selectedRows"
                  :key="invoice.InvoiceNumber"
                  type="hidden"
                  name="invoices"
                  :value="`${invoice.Id}:${invoice.InvoiceSource}`"
                />
                <b-button
                  v-if="canPrintInvoices"
                  type="submit"
                  class="btn"
                  :disabled="!exportEnabled"
                  name="format"
                  :value="'PDF'"
                >
                  {{ getContentItem("exportAsPDF") }}
                </b-button>
                <b-button
                  v-if="canPrintInvoices"
                  type="submit"
                  class="btn"
                  :disabled="!exportEnabled"
                  name="format"
                  :value="'Excel'"
                >
                  {{ getContentItem("ExportToExcel") }}
                </b-button>
                <b-button
                  v-if="canPrintInvoices"
                  type="submit"
                  class="btn"
                  :disabled="!exportEnabled"
                  name="format"
                  :value="'Word'"
                >
                  {{ getContentItem("ExportToWord") }}
                </b-button>
              </form>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="6">
              <b-form-group :label-cols="3" :label="getContentItem('perPage')">
                <b-form-select v-model="pagination.perPage" class="mt-2" size="sm" :options="pagination.perPageList" />
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group :label-cols="3" :label="getContentItem('filter')">
                <b-form-input v-model="textFilter" size="sm" :placeholder="getContentItem('filter')" />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col xs="12">
              <DnTable
                id="invoicesTable"
                ref="invoicesTableRef"
                class="blue"
                :items="invoices"
                :fields="invoiceColumns"
                :filter="textFilter"
                :sortBy="sortByField"
                :sortDescProp="sortDesc"
                :currentPage="pagination.currentPage"
                :perPage="pagination.perPage"
                :busy="isLoadingInvoices"
                :showPagination="true"
                :emptyText="'No Invoices Found'"
                @sort-changed="paginationReset"
                @update:currentPage="(val) => (pagination.currentPage = val)"
                @row-selected="RowSelected"
              >
                <template #table-busy>
                  <loading-spinner class="mx-auto" />
                </template>
                <template #HEAD_rowSelected>
                  <input type="checkbox" @change="selectAllRows" />
                </template>
                <template #rowSelected="{ row, index }">
                <input
                  type="checkbox"
                  :name="`invoice[${index}].index`"
                  v-model="row.item.rowSelected"
                  @change.stop="PropagateSelection(row)"
                />
              </template>
                <template #InvoiceNumber="{ row }">
                  <router-link
                    :to="{
                      name: 'InvoiceDetails',
                      query: { invoiceId: row.item.Id, invoiceSource: row.item.InvoiceSource },
                    }"
                  >
                    {{ row.item.InvoiceNumber }}
                  </router-link>
                </template>
                <template #ShippingDetail="{ row }">
                  <shipping-detail
                    :invoice-id="row.item.Id"
                    :invoice-source="row.item.InvoiceSource"
                    :invoice-number="row.item.InvoiceNumber"
                    @loadDetail="ShowShippingDetail"
                  />
                </template>
                <template #TotalInvoice="{ row }">
                  <currency-text :amount="row.item.TotalInvoice" :currency-code="row.item.CurrencyCode" />
                </template>
                <template #SubTotalFreight="{ row }">
                  <currency-text :amount="row.item.SubTotalFreight" :currency-code="row.item.CurrencyCode" />
                </template>
              </DnTable>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import moment from "moment";
import { mapGetters } from "vuex";

import { InvoiceService } from "@/services/InvoiceService";
import { ErrorLogService } from "@/services/ErrorLogService";
import { DealerUtil } from "@/services/";
import { FeatureFlagService } from "@/services/FeatureFlagService";

import OrdersMenu from "@/components/SubMenus/OrdersMenu";
import OrdersSearchWidgetForm from "@/components/SubMenus/OrdersSearchWidgetForm";
import { ShippingDetail, LanterShippingDetail } from "@/components/Logistics";
import CurrencyText from "@/components/Elements/CurrencyText";
import MonthSelector from "@/components/Dropdowns/MonthSelector";
import AvailableDealerCodeSelector from "@/components/Dropdowns/AvailableDealerCodeSelector";
import { formatDateTime } from "@/components/mixins/formatDateTime";
import { stringHelper } from "@/components/mixins/stringHelper";
import { paginationMixin } from "@/components/mixins/paginationMixin";
import { LoadingSpinner } from "@/components";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default {
  name: "Invoices",
  components: {
    CurrencyText,
    OrdersMenu,
    OrdersSearchWidgetForm,
    MonthSelector,
    AvailableDealerCodeSelector,
    ShippingDetail,
    LanterShippingDetail,
    LoadingSpinner,
    DnTable,
  },
  mixins: [formatDateTime, stringHelper, paginationMixin],
  data: () => ({
    allInvoiceColumns: [
      { key: "rowSelected" },
      {
        key: "InvoiceDate",
        labelKey: "date",
        sortable: true,
        formatter: "formatDateTime",
      },
      { key: "InvoiceNumber", labelKey: "invoiceNumber", sortable: true },
      { key: "ShipVia", labelKey: "shipVia", sortable: true },
      { key: "SalesOrderNumber", labelKey: "orderNumber", sortable: true },
      { key: "PurchaseOrderNumber", labelKey: "poNumber", sortable: true },
      { key: "SubTotalFreight", labelKey: "freight", sortable: true },
      { key: "ShippingDetail", labelKey: "shippingDetail", sortable: true },
      { key: "TotalInvoice", labelKey: "total", sortable: true },
    ],
    selectedRows: [],
    textFilter: null,
    allPrimaryDealerCodes: null,
    stagedDealerSelection: null,
    currentDealerSelection: null,
    stagedMonthSelection: null,
    currentMonthSelection: null,
    sortByField: "InvoiceDate",
    useUserFilter: false,
    sortDesc: true,
    exportAction: InvoiceService.getExportAction(),
    showShippingDetails: false,
    rowShippingDetails: [],
    rowInvoiceNumber: "",
    rowInvoiceId: 0,
    isLoadingInvoices: false,
    dealerFilters: [],
    invoices: [],
  }),
  computed: {
    ...mapGetters([
      "user",
      "getContentItem",
      "hasPermission",
      "canSeeAnyPrice",
      "activeAccountId",
      "availableDealerCodes",
      "getRecentComponentName",
      "getRecentComponentData",
      "IsMcfe",
    ]),
    canViewInvoices() {
      return this.hasPermission("CanSeeOrders") && !this.hasPermission("CannotViewInvoices");
    },
    canSeeInvoicePrice() {
      return this.canSeeAnyPrice && !this.hasPermission("HideInvoicePricing");
    },
    hasAccessToAllBranches() {
      return this.availableDealerCodes.length > 1 && this.user.DealerRange !== "*";
    },
    invoiceColumns() {
      if (this.canSeeInvoicePrice) {
        return this.allInvoiceColumns;
      }
      return this.allInvoiceColumns.filter((column) => {
        return column.key !== "SubTotalFreight" && column.key !== "TotalInvoice";
      });
    },
    dealerFilter() {
      if (this.currentDealerSelection.hasOwnProperty("value") && this.currentDealerSelection.value === "All Branches") {
        return this.allPrimaryDealerCodes;
      } else {
        return this.currentDealerSelection.DealerCodes.replace(/\s/g, "").split(",");
      }
    },
    monthFilter() {
      return moment(this.currentMonthSelection, "MMMM YYYY");
    },
    message() {
      let displayWithSubDealers = "";
      if (this.currentDealerSelection !== undefined) {
        displayWithSubDealers = this.currentDealerSelection.DisplayWithSubDealers;
      }
      return this.format(this.getContentItem("orderViewMessage"), displayWithSubDealers, this.currentMonthSelection);
    },
    exportEnabled() {
      return this.selectedRows.length;
    },
    canPrintInvoices() {
      return !this.hasPermission("HideInvoicePricing");
    },
  },
  watch: {
    currentMonthSelection() {
      this.SetRecentComponentData();
    },
    stagedDealerSelection() {
      this.SetRecentComponentData();
    },
  },
  created() {
    // availableDealerCodes is a bit of a misnomer as each element has more data than just a DealerCode
    this.allPrimaryDealerCodes = this.availableDealerCodes.map((dealer) => dealer.PrimaryDealerCode);

    // Match's web/src/components/Dropdowns/AvailableDealerCodeSelector.vue setup
    this.currentDealerSelection = DealerUtil.determineCurrentDealerSelection(
      this.availableDealerCodes,
      this.user,
      this.hasAccessToAllBranches
    );

    if (this.getRecentComponentName === "Invoices") {
      this.currentMonthSelection = this.getRecentComponentData.currentMonthSelection;
      this.stagedDealerSelection = this.getRecentComponentData.stagedDealerSelection;
      if (this.IsNotNullOrUndefined(this, "getRecentComponentData.stagedDealerSelection.PrimaryDealerCode")) {
        this.currentDealerSelection = this.availableDealerCodes.find(
          (dealer) => dealer.PrimaryDealerCode === this.getRecentComponentData.stagedDealerSelection.PrimaryDealerCode
        );
      }
    } else {
      this.currentMonthSelection = moment().format("MMMM YYYY");
    }

    this.allInvoiceColumns = this.allInvoiceColumns.map((column) => {
      if (typeof column === "object") {
        return Object.assign({}, column, {
          label: this.getContentItem(column.labelKey),
        });
      }
     
      return column;
    });

     this.dealerFilters = this.dealerFilter;
    this.SearchInvoices();
  },
  methods: {
    SearchInvoices: function () {
  this.isLoadingInvoices = true;
  var dealerCodesToSearch = this.dealerFilter;
  if (dealerCodesToSearch.length > 100) {
    dealerCodesToSearch = [this.user.DealerCode];
  }
  return InvoiceService.GetInvoicesByDealerCodes(
    this.activeAccountId,
    this.user.UserId,
    this.monthFilter.startOf("month").format("MM/DD/YYYY"),
    this.monthFilter.endOf("month").format("MM/DD/YYYY"),
    dealerCodesToSearch
  )
    .then((result) => {
      if (this.IsNullOrUndefined(result, "data")) {
        return;
      }
      this.paginationReset(result.data);
      let invoices = [];
      if (this.canSeeInvoicePrice) {
        invoices = Array.isArray(result.data) ? result.data : [];
      } else {
        invoices = result.data.map((record) => {
          delete record.SubTotalFreight;
          delete record.TotalInvoice;
          return record;
        });
        invoices = Array.isArray(invoices) ? invoices : [];
      }
      // Add rowSelected property to each invoice
      invoices.forEach(inv => { inv.rowSelected = false; });
      this.invoices = invoices;
      this.selectedRows = [];
      return invoices;
    })
    .catch((err) => {
      this.paginationReset([]);
      ErrorLogService.LogException(err);
      return [];
    })
    .finally(() => {
      this.isLoadingInvoices = false;
    });
},
    SetSelectedMonth: function (selectedMonth) {
      this.stagedMonthSelection = selectedMonth;
    },
    SetSelectedDealerCode: function (selectedDealer) {
      this.stagedDealerSelection = selectedDealer;
    },
    ApplyFilters: function () {
      if (this.stagedMonthSelection) {
        this.currentMonthSelection = this.stagedMonthSelection;
      }

      if (this.stagedDealerSelection) {
        this.currentDealerSelection = this.stagedDealerSelection;
      }
      this.SearchInvoices();
    },
    RowSelected(items) {
      this.selectedRows = items;
    },
    SetRecentComponentData() {
      var componentData = {};
      componentData.currentMonthSelection = this.currentMonthSelection;
      componentData.stagedDealerSelection = this.stagedDealerSelection;
      this.$store.commit("SET_RECENT_COMPONENT", { name: "Invoices", data: componentData });
    },
    selectAllRows(event) {
      const checked = event.target.checked;
      // Update rowSelected for each invoice
      this.invoices.forEach(inv => { inv.rowSelected = checked; });
      // Update selectedRows for DnTable
      this.selectedRows = checked ? [...this.invoices] : [];
      // If DnTable exposes a method to select all, call it here:
      if (this.$refs.invoicesTableRef && typeof this.$refs.invoicesTableRef.selectAll === 'function') {
        this.$refs.invoicesTableRef.selectAll(checked);
      }
    },
    PropagateSelection(row) {
      if (row.item.rowSelected) {
        // Add if not already in selectedRows
        if (!this.selectedRows.some(r => r.Id === row.item.Id && r.InvoiceSource === row.item.InvoiceSource)) {
          this.selectedRows.push(row.item);
        }
      }
      else {
        // Remove from selectedRows if unchecked
        this.selectedRows = this.selectedRows.filter(
          r => !(r.Id === row.item.Id && r.InvoiceSource === row.item.InvoiceSource)
        );
        // Uncheck "select all" checkbox when any row is deselected
        const selectAllCheckbox = document.querySelector('#invoicesTable thead input[type="checkbox"]');
        if (selectAllCheckbox) {
          selectAllCheckbox.checked = false;
        }
      }
    },
    ShowShippingDetail(details, invoiceNumber, invoiceId) {
      this.rowShippingDetails = details;
      this.rowInvoiceNumber = invoiceNumber;
      this.rowInvoiceId = invoiceId;
      this.showShippingDetails = true;
    },
    ReturnFromDetails() {
      this.showShippingDetails = false;
    },
  },
};
</script>